// @flow
import React, { useRef, useEffect, forwardRef, useState } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useRowSelect,
    useGlobalFilter,
    useAsyncDebounce,
    useExpanded,
} from 'react-table';
import classNames from 'classnames';
import { Row, Col } from 'react-bootstrap';

// components
import Pagination from './Pagination';

// Define a default UI for filtering
const GlobalFilter = ({ preGlobalFilteredRows, globalFilter, setGlobalFilter, searchBoxClass }) => {
    const count = preGlobalFilteredRows.length;
    const [value, setValue] = React.useState(globalFilter);
    const onChange = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 200);

    return (
        <div className={classNames(searchBoxClass)}>
            <span className="d-flex align-items-center">
                Buscar :{' '}
                <input
                    value={value || ''}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                    placeholder={`${count} resultados...`}
                    className="form-control w-auto ms-1"
                />
            </span>
        </div>
    );
};

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <>
            <div className="form-check">
                <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
                <label htmlFor="form-check-input" className="form-check-label"></label>
            </div>
        </>
    );
});

const Table = (props) => {
    const isSearchable = props['isSearchable'] || false;
    const isSortable = props['isSortable'] || false;
    const pagination = props['pagination'] || false;
    const isSelectable = props['isSelectable'] || false;
    const isExpandable = props['isExpandable'] || false;
    const columnasVisibles = props.columnasVisibles || {}; // Si no se proporciona, usar un objeto vacío
    const mostrarTodasLasColumnas = Object.keys(columnasVisibles).length === 0;
    const [currentSort, setCurrentSort] = useState([]);
    const [currentGlobalFilter, setCurrentGlobalFilter] = useState(''); // Nuevo estado para el filtro
    const dataTable = useTable(
        {
            columns: props['columns'],
            data: props['data'],
            initialState: { pageSize: props['pageSize'] || 10, sortBy: currentSort, globalFilter: currentGlobalFilter },
        },
        isSearchable && useGlobalFilter,
        isSortable && useSortBy,
        isExpandable && useExpanded,
        pagination && usePagination,
        isSelectable && useRowSelect,
        (hooks) => {
            isSelectable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllPageRowsSelectedProps }) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                            </div>
                        ),
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ]);

            isExpandable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        // Build our expander column
                        id: 'expander', // Make sure it has an ID
                        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
                            <span {...getToggleAllRowsExpandedProps()}>{isAllRowsExpanded ? '-' : '+'}</span>
                        ),
                        Cell: ({ row }) =>
                            // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                            // to build the toggle for expanding a row
                            row.canExpand ? (
                                <span
                                    {...row.getToggleRowExpandedProps({
                                        style: {
                                            // We can even use the row.depth property
                                            // and paddingLeft to indicate the depth
                                            // of the row
                                            paddingLeft: `${row.depth * 2}rem`,
                                        },
                                    })}>
                                    {row.isExpanded ? '-' : '+'}
                                </span>
                            ) : null,
                    },
                    ...columns,
                ]);
        }
    );
    const {
        state: { sortBy, globalFilter },
    } = dataTable;
    useEffect(() => {
        setCurrentSort(sortBy);
    }, [sortBy]);
    useEffect(() => {
        setCurrentGlobalFilter(globalFilter);
    }, [globalFilter]);

    let rows = pagination ? dataTable.page : dataTable.rows;

    return (
        <>
            <div className="d-flex justify-content-between">
                <div className="d-flex">
                    {props.customButton && <div className="mr-2">{props.customButton}</div>}{' '}
                    {props.visiblesCustum && <div className="mx-2">{props.visiblesCustum}</div>}
                </div>
                {isSearchable && (
                    <GlobalFilter
                        preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
                        globalFilter={dataTable.state.globalFilter}
                        setGlobalFilter={(value) => {
                            dataTable.setGlobalFilter(value);
                            // Verifica si la función de devolución de llamada existe antes de llamarla
                            props.handleSearchValue && props.handleSearchValue(value);
                        }}
                        searchBoxClass={props['searchBoxClass']}
                    />
                )}
            </div>
            <div className="table-responsive">
                <table
                    {...dataTable.getTableProps()}
                    className={classNames('table table-centered react-table table-bordered  ', props['tableClass'])}>
                    <thead className={props['theadClass']}>
                        {dataTable.headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    if (mostrarTodasLasColumnas || props.columnasVisibles[column.id]?.estado) {
                                        return (
                                            <th
                                                {...column.getHeaderProps(column.sort && column.getSortByToggleProps())}
                                                className={classNames({
                                                    sorting_desc: column.isSortedDesc === true,
                                                    sorting_asc: column.isSortedDesc === false,
                                                    sortable: column.sort === true,
                                                })} title="Ordenar">
                                                {column.render('Header')}
                                               
                                            </th>
                                        );
                                    } else {
                                        return null; // Si la columna está oculta, no la renderizamos
                                    }
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...dataTable.getTableBodyProps()}>
                        {(rows || []).map((row, i) => {
                            dataTable.prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        const columnId = cell.column.id;
                                        if (mostrarTodasLasColumnas || props.columnasVisibles[columnId]?.estado) {
                                            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                                        } else {
                                            return null; // Si la columna está oculta, no la renderizamos
                                        }
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {pagination && <Pagination tableProps={dataTable} sizePerPageList={props['sizePerPageList']} />}
        </>
    );
};

export default Table;
